// Catálogo de EJEMPLOS por modo — alimenta el botón "Ver ejemplos" de la
// pantalla Modos (visor: popup en desktop, fullscreen en mobile). Keyed por
// `preset.kind` del modo ('editor-video' para el Estudio de Video, que no es
// un modo V2 pero vive en la misma pantalla).
//
// ⚠️ PLACEHOLDERS: los modos que todavía no tienen ejemplos reales muestran
// su propia carátula (video + poster) como muestra, para poder probar el
// visor de punta a punta. Reemplazar cada lista con los ejemplos reales
// cuando estén los assets (en /public/images/ejemplos_modos/<modo>/).
//
// Un modo SIN ejemplos (lista vacía / key ausente) simplemente no muestra el
// botón "Ver ejemplos" en su card.

export interface ModeChatMessage {
  role: "user" | "assistant";
  text?: string;
  /** Imagen "generada" dentro del chat (burbuja del asistente). */
  imageUrl?: string;
  /** Video "generado" dentro del chat (burbuja del asistente). */
  videoUrl?: string;
  videoPoster?: string;
  /** Pausa hasta el próximo mensaje (default: la cadencia estándar del
   *  visor). Útil para dejar que un video se reproduzca antes de seguir. */
  delayAfterMs?: number;
}

export type ModeExample =
  | {
      type: "image";
      /** URL del asset (relativa a /public o absoluta). */
      url: string;
      /** Texto corto opcional que se muestra debajo del ejemplo. */
      caption?: string;
    }
  | {
      type: "video";
      url: string;
      /** Poster del video mientras carga. */
      poster?: string;
      caption?: string;
    }
  | {
      /** Conversación simulada (modo Asistente): las burbujas van
       *  apareciendo en secuencia, con imágenes/videos "generados" adentro. */
      type: "chat";
      messages: ModeChatMessage[];
      caption?: string;
    };

export const MODE_EXAMPLES: Record<string, ModeExample[]> = {
  // Ejemplos REALES (2026-07-19): imágenes generadas con el modo automático.
  "design-auto": [
    { type: "image", url: "/images/ejemplos_modos/automatico/automatico-1.jpg" },
    { type: "image", url: "/images/ejemplos_modos/automatico/automatico-2.jpg" },
    { type: "image", url: "/images/ejemplos_modos/automatico/automatico-3.jpg" },
    { type: "image", url: "/images/ejemplos_modos/automatico/automatico-4.jpg" },
  ],
  // Ejemplos REALES (2026-07-19): imágenes generadas con el modo personalizada.
  "design-modelo": [
    { type: "image", url: "/images/ejemplos_modos/personalizada/personalizada-1.jpg" },
    { type: "image", url: "/images/ejemplos_modos/personalizada/personalizada-2.jpg" },
    { type: "image", url: "/images/ejemplos_modos/personalizada/personalizada-3.jpg" },
    { type: "image", url: "/images/ejemplos_modos/personalizada/personalizada-4.jpg" },
    { type: "image", url: "/images/ejemplos_modos/personalizada/personalizada-5.jpg" },
    { type: "image", url: "/images/ejemplos_modos/personalizada/personalizada-6.jpg" },
  ],
  // Ejemplos REALES (2026-07-19): una tanda de catálogo — el mismo producto
  // (sweater) en varias vistas con modelo y solo-producto.
  "catalogo-ia": [
    { type: "image", url: "/images/ejemplos_modos/catalogo/catalogo-1.jpg" },
    { type: "image", url: "/images/ejemplos_modos/catalogo/catalogo-2.jpg" },
    { type: "image", url: "/images/ejemplos_modos/catalogo/catalogo-3.jpg" },
    { type: "image", url: "/images/ejemplos_modos/catalogo/catalogo-4.jpg" },
    { type: "image", url: "/images/ejemplos_modos/catalogo/catalogo-5.jpg" },
  ],
  // Ejemplo REAL (2026-07-19): video generado con el modo (720x1280, con voz).
  "video-influencer": [
    {
      type: "video",
      url: "/images/ejemplos_modos/influencer/influencer-1.mp4",
      poster: "/images/ejemplos_modos/influencer/influencer-1-poster.jpg",
    },
  ],
  // Ejemplo REAL (2026-07-19): conversación simulada — pedir una imagen,
  // animarla en video y cerrar con la descripción para redes.
  asistente: [
    {
      type: "chat",
      messages: [
        {
          role: "user",
          text: "Generame una imagen con el vestido verde que vendo",
        },
        {
          role: "assistant",
          imageUrl: "/images/ejemplos_modos/asistente/asistente-vestido.jpg",
        },
        { role: "user", text: "Animámela en un video" },
        {
          role: "assistant",
          videoUrl: "/images/ejemplos_modos/asistente/asistente-video.mp4",
          videoPoster:
            "/images/ejemplos_modos/asistente/asistente-video-poster.jpg",
          // El video dura ~8s: dejarlo reproducirse antes del próximo mensaje.
          delayAfterMs: 9000,
        },
        {
          role: "user",
          text: "Ahora haceme una descripción para publicarlo en redes",
        },
        {
          role: "assistant",
          text: "Verde que enamora 💚 El vestido con volados que tu verano estaba esperando: fresco, cómodo y listo para brillar en cada salida. Conseguilo ahora — link en bio ✨ #ModaVerano #VestidoVerde",
        },
      ],
    },
  ],
  // Ejemplo REAL (2026-08-20): el clip de presentación del modo, que hasta ahora
  // sólo se usaba de carátula — ADR-2026-08-20-el-ejemplo-de-animar-sale-de-su-propio-clip.
  //
  // Apunta al MISMO archivo que la carátula del modo y no a una copia en
  // `ejemplos_modos/animar-imagen/`, que es lo que decía la convención de `influencer/`.
  // Hubo una copia ahí un rato: se transcodificó el original para este visor, y después
  // se transcodificó el original EN SU LUGAR
  // (ADR-2026-08-20-el-video-de-animar-era-el-unico-que-preloadeaba). Con los dos
  // archivos ya siendo el mismo clip en H.264, la copia era 1,3 MB de repo y una
  // segunda cosa que mantener sincronizada. La convención de carpeta vale cuando el
  // ejemplo es material propio; acá el ejemplo ES la carátula.
  //
  // El poster no es opcional: `ModeExamplesModal` a propósito NO pone `muted` ("el
  // ejemplo se escucha con su voz"), así que el navegador bloquea el autoplay y sin
  // poster el visor muestra un rectángulo gris.
  "animar-imagen": [
    {
      type: "video",
      url: "/images/video_animar_imagen.mp4",
      poster: "/images/video_animar_imagen_poster.jpg",
    },
  ],
  // Ejemplos REALES (2026-07-19): anuncios generados con el modo (1024x1536).
  anuncio: [
    { type: "image", url: "/images/ejemplos_modos/anuncio/anuncio-1.jpg" },
    { type: "image", url: "/images/ejemplos_modos/anuncio/anuncio-2.jpg" },
    { type: "image", url: "/images/ejemplos_modos/anuncio/anuncio-3.jpg" },
  ],
  // Estudio de Video: sin ejemplos todavía → sin botón en su card.
  "editor-video": [],
};

/** Ejemplos del modo `kind` (lista vacía si no tiene). */
export function getModeExamples(kind?: string | null): ModeExample[] {
  if (!kind) return [];
  return MODE_EXAMPLES[kind] ?? [];
}
